import { useEffect, useRef, useState } from 'react';
import { Popover } from './Popover';

/*
 * Свой выпадающий список (дизайн-система 3.26): кнопка-поле тем же классом
 * `.select`, что и у обычного поля, и список пунктами `.menu-item`. Ставится
 * вместо браузерного `<select>` — тот рисуется системой и на телефоне
 * открывается системным колесом, из-за чего форма перестаёт выглядеть
 * приложением.
 */

export type DropdownOption = {
  value: string;
  label: string;
  /** Тихий чип `.tag` после подписи — например, «Наша компания» у своих сторон. */
  tag?: string;
  /**
   * Несколько тихих чипов `.tag.neutral` следом за `tag` — роли нашего юрлица («Собственник», «Генподрядчик»).
   * Только в пунктах открытого списка: закрытое поле несёт имя и одиночный `tag`, иначе чипы выжимают имя до «…».
   */
  tags?: string[];
};

export function Dropdown({
  id,
  value,
  options,
  onChange,
  placeholder,
  ariaLabel,
  disabled,
  missing,
  className,
}: {
  id?: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  /** Что показывать, когда значения нет среди пунктов. */
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Обязательное поле не заполнено (3.3) — рамка `.missing`. */
  missing?: boolean;
  /** Модификаторы кнопки-поля: `sm` (3.3) и место, где она стоит (`mv-pick` на полосе модели). */
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const cur = options.find((o) => o.value === value);

  // длинный список (время) открывается на выбранном пункте, а не с начала.
  // `nearest` — чтобы прокрутилось только само меню: страницу под ним трогать
  // нельзя, её прокрутка закрывает всплывающее (см. Popover).
  const sel = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) sel.current?.scrollIntoView({ block: 'nearest' });
  }, [open]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        id={id}
        className={`select${cur ? '' : ' mute'}${missing ? ' missing' : ''}${className ? ` ${className}` : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{cur ? cur.label : (placeholder ?? 'Не выбрано')}</span>
        {!!cur?.tag && <span className="tag">{cur.tag}</span>}
      </button>
      {open && (
        <Popover anchor={btn.current} onClose={() => setOpen(false)} role="listbox">
          {options.map((o) => (
            <button
              key={o.value}
              ref={o.value === value ? sel : undefined}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className={`menu-item${o.value === value ? ' on' : ''}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
              {!!o.tag && <span className="tag">{o.tag}</span>}
              {o.tags?.map((t) => (
                <span key={t} className="tag neutral">
                  {t}
                </span>
              ))}
            </button>
          ))}
        </Popover>
      )}
    </>
  );
}
